import type { Component, JSX } from 'solid-js'
import { Dynamic } from 'solid-js/web'

import Activity from 'lucide-solid/icons/activity'
import BatteryLow from 'lucide-solid/icons/battery-low'
import Building2 from 'lucide-solid/icons/building-2'
import CalendarCheck from 'lucide-solid/icons/calendar-check'
import CalendarClock from 'lucide-solid/icons/calendar-clock'
import ChartLine from 'lucide-solid/icons/chart-line'
import CircleDot from 'lucide-solid/icons/circle-dot'
import DoorOpen from 'lucide-solid/icons/door-open'
import Droplet from 'lucide-solid/icons/droplet'
import Dumbbell from 'lucide-solid/icons/dumbbell'
import FileDown from 'lucide-solid/icons/file-down'
import Flame from 'lucide-solid/icons/flame'
import Footprints from 'lucide-solid/icons/footprints'
import Gauge from 'lucide-solid/icons/gauge'
import HandHeart from 'lucide-solid/icons/hand-heart'
import Heart from 'lucide-solid/icons/heart'
import HeartPulse from 'lucide-solid/icons/heart-pulse'
import LayoutDashboard from 'lucide-solid/icons/layout-dashboard'
import ListChecks from 'lucide-solid/icons/list-checks'
import LogIn from 'lucide-solid/icons/log-in'
import Moon from 'lucide-solid/icons/moon'
import Percent from 'lucide-solid/icons/percent'
import Repeat from 'lucide-solid/icons/repeat'
import Ruler from 'lucide-solid/icons/ruler'
import Route from 'lucide-solid/icons/route'
import Maximize2 from 'lucide-solid/icons/maximize-2'
import Minimize2 from 'lucide-solid/icons/minimize-2'
import Thermometer from 'lucide-solid/icons/thermometer'
import Timer from 'lucide-solid/icons/timer'
import Trash2 from 'lucide-solid/icons/trash-2'
import TriangleAlert from 'lucide-solid/icons/triangle-alert'
import Target from 'lucide-solid/icons/target'
import TrendingUp from 'lucide-solid/icons/trending-up'
import UserCheck from 'lucide-solid/icons/user-check'
import UserMinus from 'lucide-solid/icons/user-minus'
import UserPen from 'lucide-solid/icons/user-pen'
import UserPlus from 'lucide-solid/icons/user-plus'
import Users from 'lucide-solid/icons/users'
import Watch from 'lucide-solid/icons/watch'
import Waves from 'lucide-solid/icons/waves'

/**
 * Do nome que a apresentacao publica para o desenho.
 *
 * A apresentacao NOMEIA o icone (`scope.icon = 'heart'`) e nunca importa uma
 * biblioteca - e o que permite trocar de conjunto sem tocar nela. Quem resolve o
 * nome e esta tabela, e ela e a unica do renderizador.
 *
 * ## Uma familia so, e nao duas
 *
 * O renderizador React carrega DUAS bibliotecas - Material Design Icons no menu e
 * no cabecalho, lucide nos cartoes - porque o painel antigo usava as duas e a
 * paridade visual obrigava a repetir. Aqui nao ha com o que casar: o painel e
 * desenho novo, e duas familias de icone na mesma tela e a diferenca entre parecer
 * um produto e parecer dois. Ficou lucide, que e a familia da pagina que vende.
 *
 * Nomes sem contraparte obvia ganharam o icone mais proximo, e a escolha esta
 * anotada onde nao for evidente.
 */
const ICONS: Record<string, Component<IconProps>> = {
    // ===== O menu e o cabecalho de secao =====
    graphql: LayoutDashboard,
    'chart-timeline-variant': ChartLine,
    domain: Building2,
    'account-group': Users,
    'hand-heart-outline': HandHeart,
    'account-edit': UserPen,
    // Usuarios da empresa: a pessoa com o visto, porque a area e tanto quem ja
    // esta quanto quem espera ser aprovado.
    'account-multiple-check': UserCheck,
    'account-remove': UserMinus,
    'door-open': DoorOpen,
    'file-download': FileDown,
    'format-list-text': ListChecks,
    // O Perfil de Cliente Ideal: o alvo - e o `mdiBullseyeArrow` do painel antigo.
    'bullseye-arrow': Target,

    // ===== As medicoes =====
    //
    // Os nomes vem por DOIS caminhos, e os dois tem de estar aqui: a pagina que
    // vende nomeia a metrica ('sleep', 'pressure'), e os cartoes do painel nomeiam
    // o DESENHO ('moon', 'activity'), que e a heranca do painel antigo. Um alias
    // custa uma linha; um cartao sem icone e um buraco na tela.
    sleep: Moon,
    moon: Moon,
    heart: Heart,
    // Variabilidade da frequencia cardiaca: o pulso com a linha dentro.
    hrv: HeartPulse,
    waves: Waves,
    pressure: Gauge,
    oxygen: Droplet,
    droplet: Droplet,
    temperature: Thermometer,
    thermometer: Thermometer,
    steps: Footprints,
    energy: Flame,

    // ===== Os cartoes do painel =====
    activity: Activity,
    alert: TriangleAlert,
    // Os cartoes dos vestiveis: a carga baixa, e quantos dias uma carga dura.
    'battery-low': BatteryLow,
    'calendar-check': CalendarCheck,
    'calendar-clock': CalendarClock,
    // Os dois cartoes de contagem que so este painel tem.
    company: Building2,
    ruler: Ruler,
    dumbbell: Dumbbell,
    flame: Flame,
    footprints: Footprints,
    'log-in': LogIn,
    percent: Percent,
    repeat: Repeat,
    // O anel de medicao - o aparelho, nao uma metrica.
    ring: CircleDot,
    route: Route,
    timer: Timer,
    'trash-can-outline': Trash2,
    'trending-up': TrendingUp,
    'user-check': UserCheck,
    'user-plus': UserPlus,
    users: Users,
    watch: Watch,
    // O grafico ampliado (B-43).
    maximize: Maximize2,
    minimize: Minimize2
}

export type IconProps = {
    class?: string
    size?: number
    'stroke-width'?: number
}

/** Nomes ja reclamados, para o aviso sair uma vez e nao a cada render. */
const warned = new Set<string>()

/**
 * Desenha o icone de um nome.
 *
 * Nome desconhecido nao desenha NADA - e nao um icone de erro, que espalharia um
 * simbolo estranho pelo painel. Mas em DESENVOLVIMENTO ele reclama: a tabela acima
 * e fechada e os nomes vem de dezenas de pontos da apresentacao, entao um que falte
 * so apareceria como um buraco no cartao, que e facil de nao notar. Ja aconteceu -
 * sete cartoes do painel abriram sem icone porque os nomes deles chegam por
 * argumento posicional e nao por atribuicao, e escaparam da varredura.
 */
export function Icon(props: { name?: string; class?: string; size?: number }): JSX.Element {
    const shape = () => {
        const name = props.name
        if (!name) {
            return undefined
        }

        const found = ICONS[name]
        if (!found && import.meta.env.DEV && !warned.has(name)) {
            warned.add(name)
            console.warn(`[icones] sem desenho para "${name}" - acrescente em icon-catalog.tsx`)
        }
        return found
    }

    return <Dynamic component={shape()} class={props.class} size={props.size ?? 20} stroke-width={1.75} />
}

/** Se o nome tem desenho - para a view decidir se reserva o espaco dele. */
export function hasIcon(name?: string): boolean {
    return !!name && name in ICONS
}
